import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ref) {
      // Persist ref so user can complete signup later
      try {
        localStorage.setItem("jv_ref", ref);
      } catch (e) {
        // localStorage can be blocked in private mode — non-fatal, ref just won't persist.
        console.warn("could not persist referral code:", e?.message || e);
      }
    }
  }, [ref]);

  const submit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setErr("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (!ageConfirmed) {
      setErr("You must be 16 or older to create a JobVault account.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const persistedRef = ref || (() => {
        try { return localStorage.getItem("jv_ref"); } catch { return null; }
      })();
      await register(email, password, name, persistedRef, {
        accepted_terms: acceptedTerms,
        age_confirmed: ageConfirmed,
        marketing_opt_in: marketingOptIn,
      });
      try {
        localStorage.removeItem("jv_ref");
      } catch (e) {
        console.warn("could not clear referral code:", e?.message || e);
      }
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen noise grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[var(--pastel-mint)]">
        <Link to="/" className="font-display text-2xl font-bold" data-testid="register-brand">JobVault</Link>
        <div>
          <p className="label-tiny text-[var(--brand-ink)] mb-3">new vault</p>
          <h2 className="font-display text-5xl font-semibold leading-[1.05] text-[var(--brand-ink)]">
            you, <br/>
            <span className="text-[var(--brand-primary)]">but only once.</span><br/>
            forever.
          </h2>
          <p className="mt-8 text-[var(--brand-ink)]/70 max-w-md leading-relaxed">
            takes 90 seconds to set up. saves hundreds of hours of pasting &ldquo;Greater London Area&rdquo; into forms.
          </p>
        </div>
        <p className="font-mono-d text-xs text-[var(--brand-ink)]/40">lock it once. use it forever.</p>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
          <p className="label-tiny text-[var(--brand-muted)] mb-2">/start</p>
          <h1 className="font-display text-4xl font-semibold tracking-tight mb-8">make your vault.</h1>

          {ref && (
            <div data-testid="register-ref-banner" className="card-hard rounded-md p-3 bg-[var(--pastel-mint)] text-sm mb-4">
              you&apos;ve been invited via <b className="font-mono-d">/{ref}</b> — they&apos;ll get a little badge when you sign up. nice.
            </div>
          )}

          {err && <div data-testid="register-error" className="card-hard rounded-md p-3 bg-[var(--pastel-peach)] text-sm mb-4">{err}</div>}

          <label className="label-tiny block mb-2" htmlFor="register-name">your name</label>
          <input id="register-name" data-testid="register-name" name="name" required
            autoComplete="name"
            value={name} onChange={(e) => setName(e.target.value)}
            className="w-full card-hard rounded-md px-4 py-3 mb-4 focus:ring-2 focus:ring-[var(--brand-primary)]" />

          <label className="label-tiny block mb-2" htmlFor="register-email">email</label>
          <input id="register-email" data-testid="register-email" name="email" type="email" required
            autoComplete="email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full card-hard rounded-md px-4 py-3 mb-4 focus:ring-2 focus:ring-[var(--brand-primary)]" />

          <label className="label-tiny block mb-2" htmlFor="register-password">password (min 6)</label>
          <div className="relative mb-6">
            <input id="register-password" data-testid="register-password" name="password"
              type={showPw ? "text" : "password"} required minLength={6}
              autoComplete="new-password"
              value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full card-hard rounded-md px-4 py-3 pr-12 focus:ring-2 focus:ring-[var(--brand-primary)]" />
            <button type="button" onClick={() => setShowPw((v) => !v)}
              data-testid="register-show-password"
              aria-label={showPw ? "hide password" : "show password"}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded hover:bg-[var(--pastel-yellow)]">
              {showPw ? <EyeSlash size={18}/> : <Eye size={18}/>}
            </button>
          </div>

          <div className="space-y-3 mb-6 mt-2">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="register-accept-terms"
                className="mt-1 w-4 h-4 cursor-pointer accent-[var(--brand-primary)]"
                required
              />
              <span className="text-sm leading-snug">
                I accept the{" "}
                <Link to="/terms" target="_blank" className="underline font-medium text-[var(--brand-ink)]" data-testid="register-terms-link">Terms of Service</Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" className="underline font-medium text-[var(--brand-ink)]" data-testid="register-privacy-link">Privacy Policy</Link>.
              </span>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                data-testid="register-age-confirm"
                className="mt-1 w-4 h-4 cursor-pointer accent-[var(--brand-primary)]"
                required
              />
              <span className="text-sm leading-snug">
                I confirm I am 16 or older (UK GDPR digital age of consent).
              </span>
            </label>

            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
                data-testid="register-marketing-opt-in"
                className="mt-1 w-4 h-4 cursor-pointer accent-[var(--brand-primary)]"
              />
              <span className="text-sm leading-snug text-[var(--brand-muted)]">
                Optional — email me about new features and tips (no spam, easy unsubscribe).
              </span>
            </label>
          </div>

          <button data-testid="register-submit" disabled={loading} type="submit"
            className="w-full btn-hard bg-[var(--brand-ink)] text-white rounded-md py-3.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[var(--brand-ink)]">
            {loading ? "creating…" : <>lock it in <ArrowRight weight="bold" size={16}/></>}
          </button>

          <p className="mt-6 text-sm text-[var(--brand-muted)]">
            already vaulted? <Link to="/login" data-testid="register-to-login" className="text-[var(--brand-ink)] underline font-medium">log in</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
